import React, { useEffect, useLayoutEffect, useMemo, useState, useContext, useRef } from "react";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import Swal from "sweetalert2";
import { resolveAssetUrl } from "../../api/config";
import SearchableSelect from "../../components/common/SearchableSelect";
import Modal from "../../components/common/Modal";
import DisabilitySelect from "../../components/common/DisabilitySelect";

const normalizeRegistration = (value) =>
  String(value || "").trim().toLowerCase() === "open" ? "Open" : "Closed";

const formatIsoDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const formatEventDateRange = (startDate, endDate) => {
  const start = String(startDate || "").trim();
  const end = String(endDate || startDate || "").trim();
  if (!start && !end) return "-";
  if (!start) return end;
  if (!end || end === start) return start;
  return `${start} to ${end}`;
};

const formatEventAgeScope = (value) => {
  const token = String(value || "all").trim().toLowerCase();
  if (token === "sub_junior") return "Sub-Junior";
  if (token === "junior") return "Junior";
  if (token === "senior") return "Senior";
  if (token === "sub_junior_junior" || token === "combined_sub_junior_junior") {
    return "Sub-Junior & Junior (Mixed)";
  }
  return "All";
};

const ManageEvents = () => {
  const { role, user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOpen, setShowOpen] = useState(true);
  const [showClosed, setShowClosed] = useState(true);
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);
  const [sportsLoadError, setSportsLoadError] = useState(false);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [sponsorLogoPreview, setSponsorLogoPreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [existingSponsorLogo, setExistingSponsorLogo] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [ageSettings, setAgeSettings] = useState({
    sub_junior_min_age: 14,
    junior_min_age: 17,
    senior_min_age: 20,
  });
  const [ageSettingsSaving, setAgeSettingsSaving] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    sport_id: "",
    title: "",
    start_date: "",
    end_date: "",
    date: "",
    event_end_date: "",
    end_time: "",
    location: "",
    description: "",
    convenor_name: "",
    sponsor_name: "",
    registration_fee: "",
    late_fee: "",
    late_fee_upto_date: "",
    escort_fee: "",
    max_games_per_player: 3,
    event_level: "District",
    target_age_category: "all",
    target_gender: "all",
    eligible_disabilities: [],
    district: "",
    district_id: "",
    registration: "Closed",
    image: null,
    sponsor_logo: null,
    state_quota: 3,
    linked_district_event_id: "",
  });

  const handleCloseModal = () => {
    setIsFormModalOpen(false);
    setEditing(false);
    setFormData({
      id: null,
      sport_id: "",
      title: "",
      start_date: "",
      end_date: "",
      date: "",
      event_end_date: "",
      end_time: "",
      location: "",
      description: "",
      convenor_name: "",
      sponsor_name: "",
      registration_fee: "",
      late_fee: "",
      late_fee_upto_date: "",
      escort_fee: "",
      max_games_per_player: 3,
      event_level: "District",
      target_age_category: "all",
      target_gender: "all",
      district: (role || "").toLowerCase() === "district" ? (user?.district || "") : "",
      district_id: (role || "").toLowerCase() === "district" ? String(user?.district_id || "") : "",
      registration: "Closed",
      image: null,
      sponsor_logo: null,
      state_quota: 3,
      linked_district_event_id: "",
    });
    setImagePreview(null);
    setSponsorLogoPreview(null);
    setExistingImage(null);
    setExistingSponsorLogo(null);
  };

  const linkedDistrictEventOptions = useMemo(() => {
    if (!formData.sport_id) return [];
    return events.filter(
      (e) =>
        String(e.sport_id) === String(formData.sport_id) &&
        String(e.event_level).toLowerCase() === "district" &&
        String(e.id) !== String(formData.id)
    );
  }, [events, formData.sport_id, formData.id]);

  // ✅ Load events and sports
  useEffect(() => {
    let cancelled = false;

    const extractList = (payload) => {
      const value = payload?.data?.data ?? payload?.data ?? payload;
      return Array.isArray(value) ? value : [];
    };

    const loadSports = async () => {
      setSportsLoading(true);
      setSportsLoadError(false);
      try {
        const response = await API.getSports();
        if (!cancelled) setSports(extractList(response));
      } catch {
        if (!cancelled) {
          setSports([]);
          setSportsLoadError(true);
        }
      } finally {
        if (!cancelled) setSportsLoading(false);
      }
    };

    const loadEvents = async () => {
      try {
        const response = await API.fetchAdminEvents();
        if (!cancelled) setEvents(extractList(response));
      } catch (err) {
        if (!cancelled) {
          setEvents([]);
          Swal.fire({
            icon: "error",
            title: "Unable to load events",
            text: err?.message || "Please try again.",
          });
        }
      }
    };

    const loadDistricts = async () => {
      if ((role || "").toLowerCase() === "district") {
        if (!cancelled) setDistrictOptions([]);
        return;
      }

      try {
        const response = await API.fetchDistrictOptions();
        if (!cancelled) setDistrictOptions(extractList(response));
      } catch {
        if (!cancelled) setDistrictOptions([]);
      }
    };

    loadSports();
    loadEvents();
    loadDistricts();
    if ((role || "").toLowerCase() === "admin") {
      API.fetchAgeCategorySettings()
        .then((data) => {
          if (!cancelled) setAgeSettings((prev) => ({ ...prev, ...(data || {}) }));
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [role]);

  const saveAgeCategorySettings = async () => {
    const subJunior = Number(ageSettings.sub_junior_min_age);
    const junior = Number(ageSettings.junior_min_age);
    const senior = Number(ageSettings.senior_min_age);
    if (!(subJunior >= 1 && subJunior < junior && junior < senior)) {
      Swal.fire("Invalid ranges", "The starting ages must increase from Sub-Junior to Junior to Senior.", "error");
      return;
    }

    setAgeSettingsSaving(true);
    try {
      const data = await API.updateAgeCategorySettings({
        sub_junior_min_age: subJunior,
        junior_min_age: junior,
        senior_min_age: senior,
      });
      setAgeSettings((prev) => ({ ...prev, ...(data || {}) }));
      Swal.fire("Saved", "Player event age categories have been updated.", "success");
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Unable to save age categories.", "error");
    } finally {
      setAgeSettingsSaving(false);
    }
  };

  useEffect(() => {
    if ((role || "").toLowerCase() === "district") {
      setFormData((prev) => ({
        ...prev,
        event_level: "District",
        district: user?.district || prev.district || "",
        district_id: user?.district_id ? String(user.district_id) : prev.district_id || "",
      }));
    }
  }, [role, user?.district, user?.district_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "end_time") {
        let next = String(value || "").trim();
        if (/^\d{2}:\d{2}$/.test(next)) {
          next = `${next}:00`;
        }
        return { ...prev, end_time: next };
      }
      if (name === "district_id") {
        const selected = districtOptions.find((d) => String(d.id ?? "") === value);
        return {
          ...prev,
          district_id: value,
          district: selected?.name || "",
        };
      }
      const nonUpperFields = [
        "start_date", "end_date", "date", "event_end_date", "late_fee_upto_date", "late_fee",
        "end_time", "registration", "target_age_category",
        "target_gender", "sport_id", "district_id", "event_level"
      ];
      let val = value;
      if (!nonUpperFields.includes(name)) {
        val = String(value || "").toUpperCase();
      }
      return { ...prev, [name]: val };
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    let [hours, minutes, seconds] = time.split(':');
    hours = hours?.padStart(2, '0') || '00';
    minutes = minutes?.padStart(2, '0') || '00';
    seconds = seconds?.padStart(2, '0') || '00';
    return `${hours}:${minutes}:${seconds}`;
  };

  const formRef = useRef(null);

  const getScrollParent = (node) => {
    let el = node?.parentElement;
    while (el) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      const canScroll = (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight;
      if (canScroll) return el;
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  useLayoutEffect(() => {
    if (!editing || !formRef.current) return;

    requestAnimationFrame(() => {
      const formEl = formRef.current;
      if (!formEl) return;

      const scrollParent = getScrollParent(formEl);
      const isWindowScroll =
        scrollParent === document.scrollingElement ||
        scrollParent === document.documentElement ||
        scrollParent === document.body;

      if (isWindowScroll) {
        const y = formEl.getBoundingClientRect().top + window.scrollY - 20;
        window.scrollTo({ top: y, behavior: "smooth" });
      } else {
        const parentRect = scrollParent.getBoundingClientRect();
        const formRect = formEl.getBoundingClientRect();
        const offset = formRect.top - parentRect.top + scrollParent.scrollTop - 20;
        scrollParent.scrollTo({ top: offset, behavior: "smooth" });
      }
    });
  }, [editing]);

  const roleLower = String(role || "").toLowerCase();
  const [eventLevelFilter, setEventLevelFilter] = useState(roleLower !== "district" ? "state" : "all");
  const [eventYearFilter, setEventYearFilter] = useState("all");
  const [selectedDistrictFilter, setSelectedDistrictFilter] = useState("all");

  useEffect(() => {
    if (eventLevelFilter !== "district") {
      setSelectedDistrictFilter("all");
    }
  }, [eventLevelFilter]);

  const levelFilteredEvents = useMemo(() => {
    return (Array.isArray(events) ? events : []).filter((event) => {
      const lvl = String(event.event_level || "district").toLowerCase();
      if (eventLevelFilter === "state") {
        if (lvl === "district") return false;
      } else if (eventLevelFilter === "district") {
        if (lvl !== "district") return false;
        if (selectedDistrictFilter !== "all" && String(event.district_id) !== String(selectedDistrictFilter)) return false;
      }

      if (eventYearFilter !== "all") {
        const evYear = event.event_date ? new Date(event.event_date).getFullYear() : (event.date ? new Date(event.date).getFullYear() : null);
        if (String(evYear) !== String(eventYearFilter)) return false;
      }

      return true;
    });
  }, [events, eventLevelFilter, eventYearFilter, selectedDistrictFilter]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredEvents = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();
    return levelFilteredEvents.filter((event) => {
      const isOpen = event.is_registration_open ?? (normalizeRegistration(event.registration) === "Open");
      if (!showOpen && isOpen) return false;
      if (!showClosed && !isOpen) return false;

      if (!query) return true;
      const haystack = [
        event.title,
        event.sport_name,
        event.event_level,
        event.target_age_category,
        event.target_gender,
        event.district,
        event.convenor_name,
        event.sponsor_name,
        event.event_date,
        event.date,
        event.event_end_date,
      ]
        .map((item) => String(item || "").toLowerCase())
        .join(" ");

      return haystack.includes(query);
    });
  }, [levelFilteredEvents, searchTerm, showOpen, showClosed]);

  const sortedEvents = useMemo(() => {
    if (!sortConfig.key) return filteredEvents;
    return [...filteredEvents].sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      if (Array.isArray(aVal)) aVal = aVal.join(", ");
      if (Array.isArray(bVal)) bVal = bVal.join(", ");
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredEvents, sortConfig]);

  const openCount = useMemo(
    () =>
      levelFilteredEvents.filter(
        (event) => event.is_registration_open ?? (normalizeRegistration(event.registration) === "Open")
      ).length,
    [levelFilteredEvents]
  );

  const closedCount = useMemo(
    () =>
      levelFilteredEvents.filter(
        (event) => !(event.is_registration_open ?? (normalizeRegistration(event.registration) === "Open"))
      ).length,
    [levelFilteredEvents]
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, image: file }));

    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSponsorLogoChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, sponsor_logo: file || null }));
    if (file) {
      setSponsorLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleToggle = async (id, overrideVal) => {
    try {
      const res = await API.put(`/admin/events/${id}/toggle`, { override: overrideVal });
      setEvents((prev) =>
        prev.map((event) =>
          event.id === id
            ? {
                ...event,
                registration: res?.newStatus?.includes("Open") ? "Open" : "Closed",
                registration_override: res?.override !== undefined ? res.override : overrideVal,
                is_registration_open: res?.override === 1 ? true : (res?.override === 0 ? false : res?.newStatus?.includes("Open"))
              }
            : event
        )
      );
      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: res?.note || "Event status updated successfully!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error toggling event status:", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err?.message || err?.response?.data?.message || "Error updating status.",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { sport_id, title, date, event_end_date, district, district_id } = formData;
    
    if (!sport_id || !title || !date) {
      return Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Please fill in sport, title and event date.",
      });
    }

    const today = new Date().toISOString().split("T")[0];
    const isEditing = !!formData.id;
    if (!isEditing && date && new Date(date) < new Date(today)) {
      return Swal.fire({
        icon: "warning",
        title: "Invalid Date",
        text: "Event date cannot be in the past.",
      });
    }

    if (event_end_date && date && new Date(event_end_date) < new Date(date)) {
      return Swal.fire({
        icon: "warning",
        title: "Invalid Date Range",
        text: "Event end date cannot be before event start date.",
      });
    }

    if (Number(formData.late_fee || 0) > 0 && !formData.late_fee_upto_date) {
      return Swal.fire({ icon: "warning", title: "Late Fee Date Required", text: "Select the date up to which late registrations are allowed." });
    }
    if (formData.late_fee_upto_date && (!formData.end_date || formData.late_fee_upto_date <= formData.end_date)) {
      return Swal.fire({ icon: "warning", title: "Invalid Late Fee Date", text: "Late Fee Up To must be after the normal registration end date." });
    }

    const isDistrictUser = (role || "").toLowerCase() === "district";
    if (!isDistrictUser && !String(district || "").trim() && !String(district_id || "").trim()) {
      return Swal.fire({
        icon: "warning",
        title: "District Required",
        text: "Please select district for this event.",
      });
    }

    setLoading(true);
    try {
      const fd = new FormData();

      const submitData = { ...formData };
      if ((role || "").toLowerCase() === "district" && user) {
        if (user.district) submitData.district = user.district;
        if (user.district_id) submitData.district_id = String(user.district_id);
        submitData.event_level = "District";
      }

      Object.entries(submitData).forEach(([k, v]) => {
        if (k === "image" || k === "sponsor_logo") {
          if (v instanceof File) fd.append(k, v);
        } else if (k === "eligible_disabilities") {
          fd.append("eligible_disabilities", JSON.stringify(Array.isArray(v) ? v : []));
        } else {
          if (v !== null && v !== undefined && v !== "") fd.append(k, String(v));
        }
      });

      const confirm = await Swal.fire({
        title: editing ? "Update Event?" : "Create Event?",
        text: editing
          ? "Changes will be saved permanently."
          : "A new event will be created.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: editing ? "Update" : "Create",
        cancelButtonText: "Cancel",
      });

      if (!confirm.isConfirmed) {
        setLoading(false);
        return;
      }
      if (editing) {
        await API.updateEvent(formData.id, fd);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Event updated successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await API.createEvent(fd);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Event created successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setEditing(false);
      setIsFormModalOpen(false);
      setFormData({
        id: null,
        sport_id: "",
        title: "",
        start_date: "",
        end_date: "",
        date: "",
        event_end_date: "",
        end_time: "",
        location: "",
        description: "",
        convenor_name: "",
        sponsor_name: "",
        registration_fee: "",
        late_fee: "",
        late_fee_upto_date: "",
        escort_fee: "",
        max_games_per_player: 3,
        event_level: "District",
        target_age_category: "all",
        target_gender: "all",
        eligible_disabilities: [],
        district: (role || "").toLowerCase() === "district" ? (user?.district || "") : "",
        district_id: (role || "").toLowerCase() === "district" ? String(user?.district_id || "") : "",
        registration: "Closed",
        image: null,
        sponsor_logo: null,
      });
      setImagePreview(null);
      setSponsorLogoPreview(null);
      setExistingImage(null);
      setExistingSponsorLogo(null);

      const refreshed = await API.fetchAdminEvents();
      setEvents(refreshed.data || refreshed || []);
    } catch (err) {
      console.error("Error saving event:", err);
      const msg = err?.message || err?.response?.data?.message || err?.data?.message || "Error saving event.";
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (ev) => {
    const result = await Swal.fire({
      title: "Edit Event?",
      text: "You are about to modify this event.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Edit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    let parsedEligible = [];
    if (Array.isArray(ev.eligible_disabilities)) {
      parsedEligible = ev.eligible_disabilities;
    } else if (typeof ev.eligible_disabilities === "string" && ev.eligible_disabilities.trim()) {
      try {
        parsedEligible = JSON.parse(ev.eligible_disabilities);
      } catch (_e) {
        parsedEligible = ev.eligible_disabilities.split(",").map((s) => s.trim()).filter(Boolean);
      }
    }

    setFormData({
      id: ev.id,
      sport_id: ev.sport_id ? String(ev.sport_id) : "",
      title: ev.title || "",
      start_date: formatIsoDate(ev.start_date),
      end_date: formatIsoDate(ev.end_date),
      date: formatIsoDate(ev.event_date || ev.date),
      event_end_date: formatIsoDate(ev.event_end_date || ev.event_date || ev.date),
      end_time: formatTime(ev.end_time) || "",
      location: ev.location || "",
      description: ev.description || "",
      convenor_name: ev.convenor_name || "",
      sponsor_name: ev.sponsor_name || "",
      registration_fee: ev.registration_fee ?? "",
      late_fee: ev.late_fee ?? "",
      late_fee_upto_date: formatIsoDate(ev.late_fee_upto_date),
      escort_fee: ev.escort_fee ?? "",
      max_games_per_player: ev.max_games_per_player ?? 3,
      event_level: ev.event_level || "District",
      target_age_category: ev.target_age_category || "all",
      target_gender: ev.target_gender || "all",
      eligible_disabilities: parsedEligible,
      district: ev.district || "",
      district_id: ev.district_id ? String(ev.district_id) : "",
      registration: normalizeRegistration(ev.registration),
      image: null,
      sponsor_logo: null,
      state_quota: ev.state_quota ?? 3,
      linked_district_event_id: ev.linked_district_event_id ? String(ev.linked_district_event_id) : "",
    });

    setImagePreview(
      ev.image
        ? resolveAssetUrl(ev.image, { folder: "events" })
        : null
    );
    setSponsorLogoPreview(
      ev.sponsor_logo
        ? resolveAssetUrl(ev.sponsor_logo, { folder: "events/sponsors" })
        : null
    );
    setExistingImage(ev.image || null);
    setExistingSponsorLogo(ev.sponsor_logo || null);
    setEditing(true);
    setIsFormModalOpen(true);
  };

  const handleDeleteEventFile = async (field) => {
    const label = field === "image" ? "Event Image" : "Sponsor Logo";
    const result = await Swal.fire({
      title: `Delete ${label}?`,
      text: "This file will be permanently removed from the event.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await API.deleteEventFile(formData.id, field);
      if (field === "image") {
        setImagePreview(null);
        setExistingImage(null);
      } else {
        setSponsorLogoPreview(null);
        setExistingSponsorLogo(null);
      }
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: `${label} deleted successfully.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err?.message || `Failed to delete ${label}.`,
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await API.deleteEvent(id);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Event has been deleted.",
          timer: 1500,
          showConfirmButton: false,
        });
        setEvents((prev) => prev.filter((e) => e.id !== id));
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Error deleting event.",
        });
      }
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-sky-700">Event Studio</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Manage Events</h1>
              <p className="text-sm text-slate-600 mt-1">
                Create event blocks, registration windows, and publication status from one workspace.
              </p>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    id: null, sport_id: "", title: "", start_date: "", end_date: "", 
                    date: "", event_end_date: "", end_time: "", location: "", description: "", convenor_name: "", sponsor_name: "",
                    registration_fee: "", late_fee: "", late_fee_upto_date: "", escort_fee: "", max_games_per_player: 3,
                    event_level: "District",
                    target_age_category: "all",
                    target_gender: "all",
                    district: (role || "").toLowerCase() === "district" ? (user?.district || "") : "",
                    district_id: (role || "").toLowerCase() === "district" ? String(user?.district_id || "") : "",
                    registration: "Closed", image: null, sponsor_logo: null, state_quota: 3, linked_district_event_id: ""
                  });
                  setImagePreview(null);
                  setSponsorLogoPreview(null);
                  setExistingImage(null);
                  setExistingSponsorLogo(null);
                  setIsFormModalOpen(true);
                }}
                className="mt-3 text-white font-semibold py-2 px-4 rounded-xl shadow-sm transition-all flex items-center gap-2 text-xs"
                style={{ backgroundColor: "#0369a1" }}
              >
                <span>➕</span> Create New Event
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-lg font-semibold text-slate-900">{events.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Visible</p>
                <p className="text-lg font-semibold text-slate-900">{filteredEvents.length}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Open</p>
                <p className="text-lg font-semibold text-emerald-800">{openCount}</p>
              </div>
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-rose-700">Closed</p>
                <p className="text-lg font-semibold text-rose-800">{closedCount}</p>
              </div>
            </div>
          </div>
        </section>

        {(role || "").toLowerCase() === "admin" && (
          <section className="mb-6 rounded-2xl border border-indigo-200 bg-white p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex-1">
                <h2 className="text-base font-bold text-slate-900">Player Event Age Categories</h2>
                <p className="mt-1 text-xs text-slate-500">
                  These starting ages control which category-specific events appear in player login. Each player belongs to one category only.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {[
                    ["sub_junior_min_age", "Sub-Junior starts", "Runs until the Junior starting age"],
                    ["junior_min_age", "Junior starts", "Runs until the Senior starting age"],
                    ["senior_min_age", "Senior starts", "This age and above"],
                  ].map(([field, label, help]) => (
                    <label key={field} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <span className="block text-xs font-bold text-slate-700">{label}</span>
                      <input
                        type="number"
                        min="1"
                        max="101"
                        value={ageSettings[field]}
                        onChange={(e) => setAgeSettings((prev) => ({ ...prev, [field]: e.target.value }))}
                        className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                      />
                      <span className="mt-1 block text-[10px] text-slate-500">{help}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-xs font-semibold text-indigo-700">
                  Current ranges: Sub-Junior {ageSettings.sub_junior_min_age}–&lt;{ageSettings.junior_min_age}, Junior {ageSettings.junior_min_age}–&lt;{ageSettings.senior_min_age}, Senior {ageSettings.senior_min_age}+
                </p>
              </div>
              <button
                type="button"
                onClick={saveAgeCategorySettings}
                disabled={ageSettingsSaving}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {ageSettingsSaving ? "Saving..." : "Save Age Categories"}
              </button>
            </div>
          </section>
        )}

        <Modal
          isOpen={isFormModalOpen}
          onClose={handleCloseModal}
          title={editing ? "Edit Event Block" : "Create New Event Block"}
          width="900px"
        >
          <form ref={formRef} onSubmit={handleSubmit} className="mb-0">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Sport *</label>
                <SearchableSelect 
                  name="sport_id" 
                  value={formData.sport_id} 
                  onChange={handleChange} 
                  required 
                  className="border p-2 rounded-lg w-full text-sm bg-white"
                >
                  <option value="">
                    {sportsLoading
                      ? "Loading sports..."
                      : sportsLoadError
                        ? "Unable to load sports"
                        : "Select Sport"}
                  </option>
                  {Array.isArray(sports) && sports.map((sport) => (
                    <option key={sport.id} value={sport.id}>{sport.name}</option>
                  ))}
                </SearchableSelect>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event Title *</label>
                <input 
                  type="text" 
                  name="title" 
                  placeholder="Event Title" 
                  value={formData.title} 
                  onChange={handleChange} 
                  className="border p-2 rounded-lg w-full text-sm" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Registration Start Date</label>
                <input 
                  type="date" 
                  name="start_date" 
                  value={formData.start_date} 
                  onChange={handleChange} 
                  className="border p-2 rounded-lg w-full text-sm" 
                />
                <small className="text-[10px] text-gray-500">Start date may be in the past</small>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Registration End Date & Time</label>
                <div className="flex gap-2">
                  <input 
                    type="date" 
                    name="end_date" 
                    value={formData.end_date} 
                    onChange={handleChange} 
                    className="border p-2 rounded-lg w-full text-sm" 
                  />
                  <input 
                    type="time"
                    step="1"
                    name="end_time" 
                    value={formatTime(formData.end_time)} 
                    onChange={handleChange}
                    className="border p-2 rounded-lg w-36 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event Date *</label>
                <input 
                  type="date" 
                  name="date" 
                  value={formData.date} 
                  onChange={handleChange} 
                  className="border p-2 rounded-lg w-full text-sm" 
                  required 
                />
                <small className="text-[10px] text-gray-500">Event date must not be in the past</small>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event End Date (optional)</label>
                <input
                  type="date"
                  name="event_end_date"
                  value={formData.event_end_date}
                  onChange={handleChange}
                  min={formData.date || undefined}
                  className="border p-2 rounded-lg w-full text-sm"
                />
                <small className="text-[10px] text-gray-500">Leave empty for one-day event</small>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Location</label>
                <input 
                  type="text" 
                  name="location" 
                  placeholder="Location" 
                  value={formData.location} 
                  onChange={handleChange} 
                  className="border p-2 rounded-lg w-full text-sm" 
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Registration Fee (₹)</label>
                <input 
                  type="number" 
                  name="registration_fee" 
                  placeholder="Registration Fee" 
                  value={formData.registration_fee} 
                  onChange={handleChange} 
                  className="border p-2 rounded-lg w-full text-sm" 
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Escort Fee (₹)</label>
                <input 
                  type="number" 
                  name="escort_fee" 
                  placeholder="Escort Fee" 
                  value={formData.escort_fee} 
                  onChange={handleChange} 
                  className="border p-2 rounded-lg w-full text-sm" 
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Late Fee (₹)</label>
                <input type="number" min="0" name="late_fee" placeholder="Late Fee" value={formData.late_fee} onChange={handleChange} className="border p-2 rounded-lg w-full text-sm" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Late Fee Up To</label>
                <input type="date" name="late_fee_upto_date" min={formData.end_date || undefined} value={formData.late_fee_upto_date} onChange={handleChange} className="border p-2 rounded-lg w-full text-sm" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Convenor Name</label>
                <input
                  type="text"
                  name="convenor_name"
                  placeholder="Convenor Name"
                  value={formData.convenor_name}
                  onChange={handleChange}
                  className="border p-2 rounded-lg w-full text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Sponsor Name (optional)</label>
                <input
                  type="text"
                  name="sponsor_name"
                  placeholder="Sponsor Name"
                  value={formData.sponsor_name}
                  onChange={handleChange}
                  className="border p-2 rounded-lg w-full text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event Level</label>
                <SearchableSelect
                  name="event_level"
                  value={formData.event_level}
                  onChange={handleChange}
                  className="border p-2 rounded-lg w-full text-sm bg-white"
                  disabled={(role || "").toLowerCase() === "district"}
                >
                  <option value="National">National</option>
                  <option value="State">State</option>
                  <option value="District">District</option>
                </SearchableSelect>
              </div>

              {["state", "national"].includes(String(formData.event_level || "").toLowerCase()) && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Linked District Event</label>
                    <SearchableSelect
                      name="linked_district_event_id"
                      value={formData.linked_district_event_id || ""}
                      onChange={handleChange}
                      className="border p-2 rounded-lg w-full text-sm bg-white"
                    >
                      <option value="">-- No Linked Event (Any District Event) --</option>
                      {linkedDistrictEventOptions.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.title} ({e.date})
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">State Quota (Top Rank Allowed)</label>
                    <input
                      type="number"
                      name="state_quota"
                      value={formData.state_quota}
                      onChange={handleChange}
                      className="border p-2 rounded-lg w-full text-sm"
                      min="1"
                      placeholder="e.g. 3 for top 3 positions"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event For Age Group</label>
                <SearchableSelect
                  name="target_age_category"
                  value={formData.target_age_category}
                  onChange={handleChange}
                  className="border p-2 rounded-lg w-full text-sm bg-white"
                >
                  <option value="all">All</option>
                  <option value="sub_junior">Sub-Junior</option>
                  <option value="junior">Junior</option>
                  <option value="sub_junior_junior">Sub-Junior &amp; Junior (Mixed)</option>
                  <option value="senior">Senior</option>
                </SearchableSelect>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event For Gender</label>
                <SearchableSelect
                  name="target_gender"
                  value={formData.target_gender}
                  onChange={handleChange}
                  className="border p-2 rounded-lg w-full text-sm bg-white"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </SearchableSelect>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">District</label>
                {(role || "").toLowerCase() === "district" ? (
                  <input
                    type="text"
                    value={user?.district || formData.district || ""}
                    disabled
                    className="border p-2 rounded-lg w-full bg-gray-100 cursor-not-allowed text-slate-800 text-sm"
                  />
                ) : (
                  <SearchableSelect
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleChange}
                    className="border p-2 rounded-lg w-full text-sm bg-white"
                  >
                    <option value="">Select District</option>
                    {districtOptions.map((d) => (
                      <option key={`${d.id ?? "na"}-${d.name}`} value={d.id ?? ""}>
                        {d.name}
                      </option>
                    ))}
                  </SearchableSelect>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Max Games Per Player</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  name="max_games_per_player"
                  value={formData.max_games_per_player}
                  onChange={handleChange}
                  className="border p-2 rounded-lg w-full text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Registration Status</label>
                <SearchableSelect name="registration" value={formData.registration} onChange={handleChange} className="border p-2 rounded-lg w-full text-sm bg-white">
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </SearchableSelect>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event Image</label>
                {imagePreview && (
                  <div className="mt-2 relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-24 object-cover rounded border"
                    />
                    {editing && existingImage && (
                      <button
                        type="button"
                        onClick={() => handleDeleteEventFile("image")}
                        className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shadow"
                        title="Delete image"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
                <input 
                  type="file" 
                  name="image" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  className="border p-2 rounded-lg w-full text-sm" 
                />
                <small className="text-[10px] text-gray-500 block mt-1">Optional. Max 5MB.</small>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Sponsor Logo</label>
                {sponsorLogoPreview && (
                  <div className="mt-2 relative inline-block">
                    <img
                      src={sponsorLogoPreview}
                      alt="Sponsor logo preview"
                      className="h-20 object-contain rounded border bg-white p-1"
                    />
                    {editing && existingSponsorLogo && (
                      <button
                        type="button"
                        onClick={() => handleDeleteEventFile("sponsor_logo")}
                        className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shadow"
                        title="Delete sponsor logo"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  name="sponsor_logo"
                  accept="image/*"
                  onChange={handleSponsorLogoChange}
                  className="border p-2 rounded-lg w-full text-sm"
                />
                <small className="text-[10px] text-gray-500 block mt-1">Optional. Max 5MB.</small>
              </div>
            </div>

            <div className="mt-3">
              <DisabilitySelect
                label="Eligible Disability Types (Leave empty to allow ALL disabilities)"
                selected={formData.eligible_disabilities}
                onChange={(selected) => setFormData((prev) => ({ ...prev, eligible_disabilities: selected }))}
                required={false}
              />
            </div>

            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Event Description</label>
              <textarea 
                name="description" 
                placeholder="Event Description" 
                value={formData.description} 
                onChange={handleChange} 
                rows="3" 
                className="border p-2 rounded-lg w-full text-sm"
              ></textarea>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5 pt-4 border-t">
              <div className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200 text-left max-w-md">
                💡 <strong>Tip:</strong> Keep registration window and event dates consistent to avoid approval issues.
              </div>
              <div className="flex justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-all font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-semibold transition-all shadow-sm text-sm"
                >
                  {loading ? "Saving..." : editing ? "Update Event" : "Create Event"}
                </button>
              </div>
            </div>
          </form>
        </Modal>

        {/* 📋 Events Table */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Event Library</h2>
            <p className="text-sm text-slate-600">Search, filter and operate event status from this table.</p>
          </div>
          {roleLower !== "district" && (
            <div className="flex bg-slate-200/80 p-1 rounded-xl shrink-0 self-start sm:self-auto">
              <button
                type="button"
                onClick={() => setEventLevelFilter("all")}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                All Levels
              </button>
              <button
                type="button"
                onClick={() => setEventLevelFilter("state")}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "state" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                State & Above
              </button>
              <button
                type="button"
                onClick={() => setEventLevelFilter("district")}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "district" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                District Level
              </button>
            </div>
          )}
        </div>
        <div className="p-2.5 sm:p-3 border-b border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, sport, district, level, age scope, gender scope..."
              className="border border-gray-300 rounded px-3 py-2 w-full md:max-w-md"
            />
            <SearchableSelect
              value={eventYearFilter}
              onChange={(e) => setEventYearFilter(e.target.value)}
              className="border p-2 rounded w-36 text-sm bg-white text-slate-800"
            >
              <option value="all">All Years</option>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
            </SearchableSelect>

            {eventLevelFilter === "district" && (
              <SearchableSelect
                value={selectedDistrictFilter}
                onChange={(e) => setSelectedDistrictFilter(e.target.value)}
                className="border p-2 rounded w-48 text-sm bg-white text-slate-800"
              >
                <option value="all">All Districts</option>
                {(Array.isArray(districtOptions) ? districtOptions : []).map((dist) => (
                  <option key={dist.id} value={dist.id}>
                    {dist.name}
                  </option>
                ))}
              </SearchableSelect>
            )}
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showOpen}
                onChange={(e) => setShowOpen(e.target.checked)}
              />
              Open ({openCount})
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showClosed}
                onChange={(e) => setShowClosed(e.target.checked)}
              />
              Closed ({closedCount})
            </label>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1700px] w-full border border-gray-200 text-xs sm:text-sm">
            <thead className="bg-slate-100 text-slate-700 select-none">
              <tr>
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">S.No.</th>
                {[
                  { label: "Title", key: "title" },
                  { label: "Image", key: null },
                  { label: "Sport", key: "sport_name" },
                  { label: "Level", key: "event_level" },
                  { label: "Eligible Disabilities", key: "eligible_disabilities" },
                  { label: "Age Scope", key: "target_age_category" },
                  { label: "Gender Scope", key: "target_gender" },
                  { label: "District", key: "district" },
                  { label: "Start Date", key: "start_date" },
                  { label: "End Date", key: "end_date" },
                  { label: "Event Date(s)", key: "event_date" },
                  { label: "End Time", key: "end_time" },
                  { label: "Convenor", key: "convenor_name" },
                  { label: "Sponsor", key: "sponsor_name" },
                  { label: "Fee (₹)", key: "registration_fee" },
                  { label: "Max Games", key: "max_games_per_player" },
                  { label: "Status", key: "registration" },
                ].map((col, idx) => (
                  <th
                    key={idx}
                    onClick={() => col.key && handleSort(col.key)}
                    className={`px-2.5 py-2 border border-gray-200 whitespace-nowrap ${
                      col.key ? "cursor-pointer hover:bg-slate-200 transition" : ""
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>{col.label}</span>
                      {col.key && (
                        <span className="text-[10px] text-slate-400">
                          {sortConfig.key === col.key
                            ? sortConfig.direction === "asc"
                              ? "▲"
                              : "▼"
                            : "↕"}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-2.5 py-2 border border-gray-200 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(sortedEvents) && sortedEvents.length > 0 ? (
                sortedEvents.map((e, index) => {
                  let eligibleList = [];
                  if (Array.isArray(e.eligible_disabilities)) {
                    eligibleList = e.eligible_disabilities;
                  } else if (typeof e.eligible_disabilities === "string" && e.eligible_disabilities.trim()) {
                    try { eligibleList = JSON.parse(e.eligible_disabilities); } catch (_err) {}
                  }

                  return (
                  <tr key={e.id} className="text-center hover:bg-gray-50 transition align-middle">
                    <td className="px-2.5 py-2 border border-gray-200 font-semibold">{index + 1}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.title}</td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      {e.image ? (
                        <img
                          src={resolveAssetUrl(e.image, { folder: "events" })}
                          alt={e.title}
                          className="h-9 w-auto object-cover mx-auto"
                        />
                      ) : (
                        <span className="text-xs text-gray-500">No image</span>
                      )}
                    </td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.sport_name + "-" + e.sport_id}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.event_level || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      {eligibleList.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center max-w-[180px] mx-auto">
                          {eligibleList.map((d, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              {d}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-emerald-700 font-medium">All Allowed</span>
                      )}
                    </td>
                    <td className="px-2.5 py-2 border border-gray-200">{formatEventAgeScope(e.target_age_category)}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.target_gender || "all"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.district || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.start_date || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.end_date || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{formatEventDateRange(e.event_date || e.date, e.event_end_date)}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{formatTime(e.end_time) || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.convenor_name || "-"}</td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      <div className="flex flex-col items-center gap-1">
                        <span>{e.sponsor_name || "-"}</span>
                        {e.sponsor_logo ? (
                          <img
                            src={resolveAssetUrl(e.sponsor_logo, { folder: "events/sponsors" })}
                            alt={e.sponsor_name || "Sponsor"}
                            className="h-6 object-contain"
                          />
                        ) : null}
                      </div>
                    </td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.registration_fee}</td>
                    <td className="px-2.5 py-2 border border-gray-200">{e.max_games_per_player ?? 3}</td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      {e.registration_override === 1 ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-200 text-green-800 border border-green-300" title="Manually forced Open by Admin">
                          Open (Forced)
                        </span>
                      ) : e.registration_override === 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-200 text-red-800 border border-red-300" title="Manually forced Closed by Admin">
                          Closed (Forced)
                        </span>
                      ) : e.is_registration_open ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200" title="Open by auto-close schedule">
                          Open (Auto)
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700 border border-gray-300" title="Closed by auto-close schedule">
                          Closed (Auto)
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-2 border border-gray-200">
                      <div className="flex items-center justify-center gap-1.5 flex-nowrap overflow-x-auto whitespace-nowrap pb-0.5">
                        {(() => {
                          const isCurrentlyOpen = e.registration_override === 1 ? true : (e.registration_override === 0 ? false : !!e.is_registration_open);
                          const actions = [];
                          if (isCurrentlyOpen) {
                            actions.push({
                              label: "Force Close",
                              colorClass: "bg-red-600 hover:bg-red-700",
                              nextOverride: 0,
                              title: "Manually force registration closed"
                            });
                          } else {
                            actions.push({
                              label: "Force Open",
                              colorClass: "bg-emerald-600 hover:bg-emerald-700",
                              nextOverride: 1,
                              title: "Manually force registration open"
                            });
                          }
                          if (e.registration_override !== null && e.registration_override !== undefined) {
                            actions.push({
                              label: "Reset to Auto",
                              colorClass: "bg-blue-600 hover:bg-blue-700",
                              nextOverride: null,
                              title: "Restore date-based automatic opening/closing"
                            });
                          }
                          return actions.map((act, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleToggle(e.id, act.nextOverride, isCurrentlyOpen)}
                              className={`px-2 py-0.5 rounded text-[10px] sm:text-xs text-white transition ${act.colorClass}`}
                              title={act.title}
                            >
                              {act.label}
                            </button>
                          ));
                        })()}
                        <button
                          onClick={() => handleEdit(e)}
                          className="bg-yellow-400 hover:bg-yellow-500 text-white px-2.5 py-1 rounded text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(e.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 rounded text-xs"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
              ) : (
                <tr>
                  <td colSpan="17" className="p-2.5 text-gray-500 text-center">
                    No events found for current search/filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </section>
      </div>
    </>
  );
};

export default ManageEvents;
